import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { getAllRoles, UserKeys } from '../../../api/users-api';
import { UserRole } from '../../../../types';
import { useMemo, useState } from 'react';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import RoleManagerGridToolbar from './role-manager-grid-toolbar';
import CloneRoleDialog from './clone-role-dialog';
import { useNavigate } from 'react-router-dom';
import CreateRoleDialog from './create-role-dialog';
import DeleteRoleDialog from './delete-role-dialog';
import RoleRenameDialog from './rename-role-dialog';
import { Messages } from '../../../utils/message-constants';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';

import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Delete from '@mui/icons-material/Delete';
import DriveFileRenameOutline from '@mui/icons-material/DriveFileRenameOutline';
import MiscellaneousServices from '@mui/icons-material/MiscellaneousServices';

interface RoleManagerGridProps {
  administrationRoles: string[];
  changeLogRoles: string[];
}

export default function RoleManagerGrid({
  administrationRoles,
  changeLogRoles,
}: RoleManagerGridProps) {
  const theme = useTheme();
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [openCreateRole, setOpenCreateRole] = useState(false);
  const [openCloneRole, setOpenCloneRole] = useState(false);
  const [openDeleteRole, setOpenDeleteRole] = useState(false);
  const [openRenameRole, setOpenRenameRole] = useState(false);
  const navigate = useNavigate();

  const {
    data: roles,
    isLoading: rolesLoading,
    isFetching: rolesFetching,
  } = useQuery({
    queryKey: [UserKeys.Roles],
    queryFn: () => {
      return getAllRoles();
    },
    meta: {
      errorMessage:
        'There was an issue loading the roles. Please refresh and try again.',
    },
  });

  const roleNames = roles?.map((role) => role.roleName) || [];

  const columns: GridColDef<UserRole>[] = useMemo(
    () => [
      {
        field: 'createdDate',
        headerName: 'Created Date',
        width: 185,
        type: 'dateTime',
        valueGetter: (value: number) => {
          const date = new Date(value);
          if (value === 0) {
            return null;
          } else {
            return date;
          }
        },
        valueFormatter: (params: Date | null) => {
          if (params === null) {
            return 'N/A';
          } else {
            return params.toLocaleString();
          }
        },
      },
      // {
      //   field: 'creatorEmail',
      //   headerName: 'Created By',
      //   flex: 2,
      //   display: 'flex',
      //   renderCell: (params) => {
      //     if (params.value) {
      //       return <AvatarChip email={params.value} type="User" />;
      //     } else {
      //       return 'N/A';
      //     }
      //   },
      // },
      {
        field: 'roleName',
        headerName: 'Role Name',
        flex: 1,
      },
      {
        field: 'roleType',
        headerName: 'Role Type',
        width: 100,
        type: 'singleSelect',
        valueOptions: [
          { label: 'Admin', value: 'admin' },
          { label: 'User', value: 'user' },
        ],
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'default',
        headerName: 'Is Default',
        type: 'boolean',
        width: 120,
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'desc',
        headerName: 'Description',
        flex: 2,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        getActions: (params) => {
          const buttonArray = [];
          if (params.row.default) {
            buttonArray.push(
              <Tooltip arrow title={'Default roles cannot be deleted.'}>
                <span>
                  <GridActionsCellItem
                    label="Delete"
                    disabled
                    icon={<Delete />}
                  />
                </span>
              </Tooltip>
            );
          } else {
            buttonArray.push(
              <Tooltip
                arrow
                title={
                  !isAbleToSeeUIElement(
                    administrationRoles,
                    AdministrationRoles.DeleteRole.value
                  )
                    ? Messages.Errors.Permission.Acting(
                        AdministrationRoles.DeleteRole.name
                      )
                    : 'Delete'
                }
              >
                <span>
                  <GridActionsCellItem
                    label="Delete"
                    icon={<Delete />}
                    onClick={() => {
                      setSelectedRole(params.row);
                      setOpenDeleteRole(true);
                    }}
                    disabled={
                      !isAbleToSeeUIElement(
                        administrationRoles,
                        AdministrationRoles.DeleteRole.value
                      )
                    }
                  />
                </span>
              </Tooltip>
            );
          }
          if (params.row.default) {
            buttonArray.push(
              <GridActionsCellItem
                label={'Rename'}
                icon={<DriveFileRenameOutline />}
                onClick={() => {
                  setSelectedRole(params.row);
                  setOpenRenameRole(true);
                }}
                showInMenu
                disabled
              />
            );
          } else {
            buttonArray.push(
              <GridActionsCellItem
                label={'Rename'}
                icon={
                  <Tooltip arrow title={'Rename'}>
                    <DriveFileRenameOutline />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedRole(params.row);
                  setOpenRenameRole(true);
                }}
                showInMenu
                disabled={
                  !isAbleToSeeUIElement(
                    administrationRoles,
                    AdministrationRoles.RenameRole.value
                  )
                }
              />
            );
          }
          buttonArray.push(
            <GridActionsCellItem
              label="Clone"
              icon={
                <Tooltip arrow title={'Clone'}>
                  <ContentCopy />
                </Tooltip>
              }
              showInMenu
              onClick={() => {
                setSelectedRole(params.row);
                setOpenCloneRole(true);
              }}
              disabled={
                !isAbleToSeeUIElement(
                  administrationRoles,
                  AdministrationRoles.CreateRole.value
                )
              }
            />
          );
          return buttonArray;
        },
      },
    ],
    [administrationRoles]
  );
  const toolbarProps = useMemo(
    () => ({
      administrationRoles,
      setOpenCreateRole,
    }),
    [administrationRoles]
  );

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: useToolbar(RoleManagerGridToolbar, toolbarProps),
          ...createGridOverlays('Roles', <MiscellaneousServices />),
        }}
        rows={roles || []}
        getRowClassName={(params) => {
          return params.row.default ? 'default-role' : '';
        }}
        getRowId={(row) => row.roleName}
        columns={columns}
        loading={rolesLoading || rolesFetching}
        onRowClick={(row: GridRowParams<UserRole>) => {
          navigate(`./${encodeURIComponent(row.row.roleName)}`);
        }}
        defaultGridState={{
          sorting: {
            sortModel: [
              {
                field: 'default',
                sort: 'desc',
              },
              {
                field: 'roleName',
                sort: 'asc',
              },
            ],
          },
        }}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
          '& .default-role': {
            backgroundColor: theme.palette.primary.light,
            ':hover': {
              backgroundColor: theme.palette.primary.main,
            },
          },
        }}
      />
      {selectedRole && openCloneRole ? (
        <CloneRoleDialog
          open={openCloneRole}
          handleClose={() => setOpenCloneRole(false)}
          selectedRole={selectedRole}
          changeLogRoles={changeLogRoles}
          roleNames={roleNames}
        />
      ) : null}
      {openCreateRole ? (
        <CreateRoleDialog
          open={openCreateRole}
          handleClose={() => {
            setOpenCreateRole(false);
          }}
          changeLogRoles={changeLogRoles}
          roleNames={roleNames}
        />
      ) : null}
      {selectedRole && openDeleteRole ? (
        <DeleteRoleDialog
          open={openDeleteRole}
          handleClose={() => setOpenDeleteRole(false)}
          selectedRole={selectedRole}
          changeLogRoles={changeLogRoles}
          roleNames={roleNames}
        />
      ) : null}
      {selectedRole && openRenameRole ? (
        <RoleRenameDialog
          open={openRenameRole}
          handleClose={() => setOpenRenameRole(false)}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          changeLogRoles={changeLogRoles}
          roleNames={roleNames}
        />
      ) : null}
    </>
  );
}
